import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { formatSandboxPrice, type SandboxPaymentHistoryItem } from '../../utils/sandboxCourseApi';
import { fetchSandboxPaymentHistory } from '../../redux/sandbox/actions';
import { useDispatchPromise } from '../../utils/AppUtils';

function statusLabel(status: string) {
    const s = String(status || '').toLowerCase();
    if (s === 'held' || s === 'pending') return 'Pending';
    if (s === 'completed' || s === 'approved') return 'Completed';
    if (s === 'refunded') return 'Refunded';
    if (s === 'rejected' || s === 'failed') return 'Failed';
    return status || '—';
}

function typeLabel(type: SandboxPaymentHistoryItem['type']) {
    if (type === 'course') return 'Course';
    if (type === 'office_hours') return 'Office hours';
    return 'Payout';
}

function formatWhen(value?: string) {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Teacher Studio: course + office hours credits and withdrawals.
 */
export function SandboxPaymentHistoryPanel() {
    const dispatchPromise = useDispatchPromise();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState<SandboxPaymentHistoryItem[]>([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res: any = await dispatchPromise(fetchSandboxPaymentHistory({ page: 1, limit: 50 }));
            setItems(res?.items || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [dispatchPromise]);

    useEffect(() => {
        void load();
    }, [load]);

    return (
        <Box
            sx={{
                borderRadius: 3,
                bgcolor: OBSIDIAN.surfaceContainer,
                p: 2,
                mb: 3,
            }}>
            <Typography
                sx={{
                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                    fontWeight: 700,
                    fontSize: 16,
                    color: OBSIDIAN.onSurface,
                    mb: 1.5,
                }}>
                Payment history
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ color: OBSIDIAN.primary }} />
                </Box>
            ) : items.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                    No payments yet. Course enrollments and office hours will show up here.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {items.map(item => {
                        const credit = item.direction === 'credit';
                        return (
                            <Box
                                key={item.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 1.5,
                                    alignItems: 'flex-start',
                                    p: 1.25,
                                    borderRadius: 2,
                                    bgcolor: OBSIDIAN.surfaceContainerHigh,
                                }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: OBSIDIAN.onSurface,
                                            fontFamily: OBSIDIAN_BODY_FONT,
                                        }}>
                                        {item.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            color: OBSIDIAN.onSurfaceVariant,
                                            mt: 0.25,
                                        }}>
                                        {typeLabel(item.type)}
                                        {item.subtitle ? ` · ${item.subtitle}` : ''}
                                        {item.created_at ? ` · ${formatWhen(item.created_at)}` : ''}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            color: OBSIDIAN.onSurfaceVariant,
                                            mt: 0.25,
                                        }}>
                                        {statusLabel(item.status)}
                                        {item.status === 'held' && item.available_at
                                            ? ` · releases ${formatWhen(item.available_at)}`
                                            : ''}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        flexShrink: 0,
                                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                                        fontWeight: 700,
                                        fontSize: 14,
                                        color: credit ? OBSIDIAN.secondary : OBSIDIAN.primary,
                                    }}>
                                    {credit ? '+' : ''}
                                    {formatSandboxPrice(item.amount_cents)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
